import { useState } from 'react';

import { IconButton } from 'Components/Buttons/IconButton';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { Text } from 'Components/Text/Text';
import { ICON_DELETE_REMOVE } from 'Config/Icons';
import { TPRM_IRQ_TARGET_COMPLETION_DATE_STARTING_NEW_WORKFLOW, TPRM_IRQ_TARGET_COMPLETION_DATE_UPDATING_VALUE } from 'Config/Tooltips';
import { iso8601ToJsDate, iso8601ToUsDateLong, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getNextDueDate } from 'Helpers/ScheduleFrequency/ScheduleFrequency';
import { ScheduleV2 } from 'Models/TPRM';

import styles from './IrqTargetCompletionDateSelection.module.css';

export type ArgsForGettingIrqTargetCompletionDateSelectionProps =
    | {
          action: 'updateExistingSelection';
          currentAssessmentWorkflowIrqTargetCompletionDate?: string;
          irqTargetCompletionSetting?: never;
      }
    | {
          action: 'startServiceAssessmentWorkflow';
          currentAssessmentWorkflowIrqTargetCompletionDate?: string;
          irqTargetCompletionSetting?: ScheduleV2;
      };

const getDates = (args: ArgsForGettingIrqTargetCompletionDateSelectionProps): [Date | undefined, Date | undefined] => {
    const recommendedDate = args.irqTargetCompletionSetting ? iso8601ToJsDate(getNextDueDate(new Date(), args.irqTargetCompletionSetting.schedule_number.toString(), args.irqTargetCompletionSetting.schedule_frequency)) : undefined;
    const currentTargetDate = args.currentAssessmentWorkflowIrqTargetCompletionDate ? iso8601ToJsDate(args.currentAssessmentWorkflowIrqTargetCompletionDate) : undefined;
    const defaultDate = args.action === 'startServiceAssessmentWorkflow' && currentTargetDate === undefined ? recommendedDate : currentTargetDate;
    return [recommendedDate, defaultDate];
};

export const getDefaultIrqTargetCompletionDate = (args: ArgsForGettingIrqTargetCompletionDateSelectionProps): Date | undefined => {
    return getDates(args)[1];
};

export const getIrqTargetCompletionDateSelectionProps = (args: ArgsForGettingIrqTargetCompletionDateSelectionProps, onDateSelected: (date: Date | undefined) => void): IrqTargetCompletionDateSelectionProps => {
    const [recommendedDate, defaultDate] = getDates(args);
    const tooltipText = args.action === 'startServiceAssessmentWorkflow' ? TPRM_IRQ_TARGET_COMPLETION_DATE_STARTING_NEW_WORKFLOW : TPRM_IRQ_TARGET_COMPLETION_DATE_UPDATING_VALUE;

    const recommendationText = (() => {
        if (args.action === 'startServiceAssessmentWorkflow') {
            return recommendedDate ? `The recommended date is ${iso8601ToUsDateLong(jsDateToIso8601(recommendedDate))}.` : 'There is no recommended date.';
        } else {
            return undefined;
        }
    })();

    return { defaultDate, onDateSelected, recommendationText, tooltipText };
};

export interface IrqTargetCompletionDateSelectionProps {
    defaultDate?: Date;
    onDateSelected: (date: Date | undefined) => void;
    recommendationText?: string;
    tooltipText?: string;
}

/**
 * Renders a date picker, a tooltip, and (maybe) recommendation text.
 * The date selected in the date picker will be used to set the target completion date for the third-party service's IRQ.
 *
 * Used in the following scenarios:
 * * The user wants to update the date from the Third Parties and Services listing.
 * * The user is creating and finalizing a new service, which will begin the assessment workflow.
 * * The user is finalizing an existing service, which will begin the assessment workflow.
 * * The user is starting or restarting the assessment workflow from the Service Dashboard.
 *
 * The date populated by default and the recommendation text follow these rules:
 * * If the user is simply updating their existing selection, the date is pre-populated with the date already set by the user (which may be undefined), and there is no recommendation text.
 * * If the user is starting a new assessment workflow, the recommendation text recommends the date based on the IRQ target completion date configured in Settings, and:
 * * * If the user has already set a date, the date picker is pre-populated with that date.
 * * * Otherwise, the date picker is pre-populated with the recommended date (which may be undefined).
 */
export const IrqTargetCompletionDateSelection = (props: IrqTargetCompletionDateSelectionProps) => {
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date | undefined>(props.defaultDate);

    return (
        <>
            <div className={styles.datePickerAndClearIcon}>
                <FormFieldDatePicker
                    dateFormat="MM/dd/yyyy"
                    selected={selectedIrqTargetCompletionDate}
                    handleChange={(selectedDate: Date | undefined) => {
                        setSelectedIrqTargetCompletionDate(selectedDate);
                        props.onDateSelected(selectedDate);
                    }}
                    formFieldId="irqTargetCompletionDateSelection"
                    formFieldLabel="Inherent Risk Questionnaire Target Completion Date"
                    placeholder={'MM/DD/YYYY'}
                    tooltip={props.tooltipText}
                />
                <div className={styles.clearIcon}>
                    <IconButton
                        aria-label="clear-date"
                        onClick={() => {
                            setSelectedIrqTargetCompletionDate(undefined);
                            props.onDateSelected(undefined);
                        }}
                        fontAwesomeImage={ICON_DELETE_REMOVE}
                    />
                </div>
            </div>
            {props.recommendationText && <Text>{props.recommendationText}</Text>}
        </>
    );
};
